import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Clock, FileUp, Send, Sparkles, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageShell } from "@/pages/dashboard/RolePages";
import DiagnosticSeal from "@/components/DiagnosticSeal";
import "@/components/DiagnosticsExperience.css";
import api, { formatApiError } from "@/lib/api";

const Estudios = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeFromUrl = searchParams.get("resume");
  const [items, setItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [selected, setSelected] = useState(null);
  const [consultation, setConsultation] = useState("");
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [error, setError] = useState("");
  const [errorAction, setErrorAction] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const submitRef = useRef(false);
  const [progress, setProgress] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [resumeId, setResumeId] = useState(null);
  const [savedMaterials, setSavedMaterials] = useState([]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError("");
    try {
      const { data } = await api.get("/api/diagnostics/catalog");
      const fresh = Array.isArray(data) ? data : [];
      setItems(fresh);
      return fresh;
    } catch (e) {
      setItems([]);
      setCatalogError(formatApiError(e));
      return [];
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!resumeFromUrl) return;
    let cancelled = false;
    api.get(`/api/diagnostics/${encodeURIComponent(resumeFromUrl)}/requirements`)
      .then(({ data }) => {
        if (cancelled) return;
        setResumeId(data.id);
        setSavedMaterials(data.materials || []);
        setSelected({ slug: data.catalog_slug, title: data.catalog_title, energy_cost: data.energy_cost,
          intake_fields: data.intake_fields || [], material_requirements: data.material_requirements || [] });
        setConsultation(data.consultation || "");
        setAnswers(data.answers || {});
        setFiles({}); setError(""); setErrorAction(null); setSuccess(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status === 409
          ? "El pedido ya cambió de estado. Consulta Mis Diagnósticos; no vuelvas a pagarlo."
          : `No se pudo recuperar el pedido registrado: ${formatApiError(err)}`);
        setErrorAction({ label: "Ver mis diagnósticos", path: "/app/mis-diagnosticos" });
      });
    return () => { cancelled = true; };
  }, [resumeFromUrl]);

  const open = (item) => {
    setSelected(item); setConsultation(""); setAnswers({}); setFiles({}); setError(""); setErrorAction(null); setProgress(""); setSuccess(null);
    setResumeId(null); setSavedMaterials([]);
    setIdempotencyKey(globalThis.crypto?.randomUUID?.() || `diag-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  };
  const close = () => {
    if (busy) return;
    setSelected(null);
    if (resumeId) {
      setError("Tu solicitud ya está pagada. Puedes continuar la carga desde Mis Diagnósticos sin un nuevo cobro.");
      setErrorAction({ label: "Ver mis diagnósticos", path: "/app/mis-diagnosticos" });
    }
  };
  const fileCount = useMemo(() => Object.values(files).reduce((sum,list)=>sum+(list?.length||0),0),[files]);

  const validateInformation = () => {
    if (!selected) return "Selecciona un diagnóstico.";
    if (consultation.trim().length < 3) return "Escribe qué deseas consultar.";
    for (const field of resumeId ? [] : selected.intake_fields || []) {
      const value = answers[field.id];
      const empty = value === undefined || value === null || String(value).trim() === "";
      if (field.required && empty) return `Completa el campo: ${field.label}`;
      if (!empty && field.type === "number" && Number.isNaN(Number(value))) return `${field.label}: escribe un número válido.`;
      if (!empty && field.type === "yes_no" && !["Sí", "No"].includes(value)) return `${field.label}: selecciona Sí o No.`;
      if (!empty && field.type === "choice" && !(field.options || []).includes(value)) return `${field.label}: selecciona una opción válida.`;
    }
    for (const req of selected.material_requirements || []) {
      const chosen = files[req.id] || [];
      const minimum = Number(req.min_files ?? (req.required ? 1 : 0));
      const maximum = Number(req.max_files || 1);
      const saved = savedMaterials.filter((row) => row.requirement_id === req.id).length;
      if (chosen.length + saved < minimum) return `${req.label}: selecciona mínimo ${Math.max(0, minimum - saved)} archivo(s) adicionales.`;
      if (chosen.length + saved > maximum) return `${req.label}: puedes agregar máximo ${Math.max(0, maximum - saved)} archivo(s).`;
      for (const file of chosen) {
        if (file.size > 25 * 1024 * 1024) return `${file.name}: el archivo supera 25 MB.`;
      }
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitRef.current) return;
    setError(""); setErrorAction(null); setSuccess(null);
    const validationError = validateInformation();
    if (validationError) { setError(validationError); return; }
    submitRef.current = true;
    setBusy(true);
    let draftCreatedInThisAttempt = false;
    try {
      setProgress("Guardando la información…");
      // A resumed request is already paid: never call the wallet debit endpoint again.
      const { data: draft } = resumeId
        ? await api.get(`/api/diagnostics/${encodeURIComponent(resumeId)}/requirements`)
        : await api.post("/api/diagnostics/request", {
          catalog_slug: selected.slug,
          consultation: consultation.trim(),
          answers,
          expected_energy_cost: Number(selected.energy_cost || 0),
          idempotency_key: idempotencyKey,
        }, { timeout: 30000 });
      draftCreatedInThisAttempt = true;
      setResumeId(draft.id);
      setSavedMaterials(draft.materials || []);
      if (draft.status !== "draft_requirements") {
        setSelected(null);
        setSuccess({ id: draft.id, energy_cost: draft.energy_cost, title: draft.catalog_title || selected.title });
        return;
      }
      const existingCounts = (draft.materials || []).reduce((counts, row) => {
        counts[row.requirement_id] = (counts[row.requirement_id] || 0) + 1;
        return counts;
      }, {});
      let uploaded = 0;
      for (const req of selected.material_requirements || []) {
        const alreadyUploaded = existingCounts[req.id] || 0;
        const chosen = files[req.id] || [];
        const maximum = Number(req.max_files || 1);
        const availableSlots = Math.max(0, maximum - alreadyUploaded);
        for (const file of chosen.slice(0, availableSlots)) {
          uploaded += 1;
          setProgress(`Subiendo foto o archivo ${uploaded} de ${fileCount}…`);
          const payload = new FormData();
          payload.append("file", file);
          const { data: saved } = await api.post(`/api/diagnostics/${draft.id}/materials`, payload, {
            params: { requirement_id: req.id },
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          setSavedMaterials(saved.materials || []);
          existingCounts[req.id] = (existingCounts[req.id] || 0) + 1;
        }
        const minimum = Number(req.min_files ?? (req.required ? 1 : 0));
        if ((existingCounts[req.id] || 0) < minimum) {
          throw new Error(`${req.label}: selecciona y sube mínimo ${minimum} archivo(s).`);
        }
      }
      setProgress("Confirmando requisitos…");
      const { data: completed } = await api.post(`/api/diagnostics/${draft.id}/submit-requirements`, null, { timeout: 30000 });
      if (completed.status !== "available") {
        throw new Error("El diagnóstico se guardó, pero todavía no está disponible en la Bolsa. Consulta su estado.");
      }
      setSelected(null);
      setSuccess({ id: completed.id, energy_cost: completed.energy_cost, title: completed.catalog_title || selected.title });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "DIAGNOSTIC_PRICE_CHANGED") {
        const fresh = await loadCatalog();
        const updated = fresh.find((item) => item.slug === selected?.slug);
        if (updated) setSelected(updated);
        setError(`El costo cambió a ${fmtEnergy(detail.current_energy_cost)}. Revisa el valor actualizado y vuelve a confirmar.`);
      } else if (detail?.code === "INSUFFICIENT_ENERGY") {
        setError(`Saldo insuficiente. Tienes ${fmtEnergy(detail.balance)} y este diagnóstico requiere ${fmtEnergy(detail.required)}.`);
        setErrorAction({ label: "Recargar Energía KD9", path: detail.redirect || "/app/energia-kd" });
      } else if (detail?.code === "WALLET_FROZEN") {
        setError(detail.message || "Tu Wallet de Energía KD9 está congelada.");
      } else if (detail?.code === "IDEMPOTENCY_KEY_REUSED") {
        setError("Esta operación ya fue utilizada con otra solicitud. Revisa Mis Diagnósticos antes de iniciar una nueva para evitar un cobro duplicado.");
        setErrorAction({ label: "Ver mis diagnósticos", path: "/app/mis-diagnosticos" });
      } else if (detail?.code === "TRANSACTION_RETRY_EXHAUSTED" || detail?.code === "IDEMPOTENCY_CONFLICT") {
        setError(detail.message || "No fue posible completar el débito. Tu saldo no fue consumido.");
      } else if (resumeId || draftCreatedInThisAttempt) {
        setError(`El pedido está registrado y pagado, pero falta completar la carga: ${formatApiError(err)}. Reintenta aquí o desde Mis Diagnósticos; NO se cobrará de nuevo.`);
        setErrorAction({ label: "Ver mis diagnósticos", path: "/app/mis-diagnosticos" });
      } else {
        setError(`No se pudo confirmar la solicitud: ${formatApiError(err)}. Revisa Mis Diagnósticos antes de intentar una compra nueva.`);
        setErrorAction({ label: "Ver mis diagnósticos", path: "/app/mis-diagnosticos" });
      }
    } finally {
      submitRef.current = false;
      setBusy(false); setProgress("");
    }
  };


  const renderAnswer = (field) => {
    const common = { value: answers[field.id] ?? "", onChange: (e) => setAnswers({...answers,[field.id]:e.target.value}), className:"kd9-input w-full mt-1 p-3" };
    if (field.type === "long_text") return <textarea {...common} rows={4}/>;
    if (field.type === "choice") return <select {...common}><option value="">Selecciona…</option>{(field.options||[]).map(x=><option key={x} value={x}>{x}</option>)}</select>;
    if (field.type === "yes_no") return <select {...common}><option value="">Selecciona…</option><option value="Sí">Sí</option><option value="No">No</option></select>;
    return <input {...common} type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}/>;
  };
  const acceptFor = (kind) => ({image:"image/*,.heic,.heif",document:".pdf,.doc,.docx,.txt",audio:"audio/*",video:"video/*",any:"image/*,audio/*,video/*,.pdf,.doc,.docx,.txt"}[kind] || undefined);
  const fmtEnergy = (n) => `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(n || 0)} Energía KD9`;

  return <div className="kd9-diagnostics kd9-diagnostics-workspace"><PageShell badge={<DiagnosticSeal compact />} kicker="Usuario · Diagnósticos" title="SOLICITAR DIAGNÓSTICO"
    desc="Completa la información y adjunta el material. Al solicitar, el costo en energía se compara con tu saldo y se descuenta automáticamente de tu Wallet de Energía KD9.">
    {error && <div className="diagnostic-state diagnostic-state-error kd9-card p-4 mb-4 text-sm" style={{color:"var(--diag-error)"}}>
      <div>{error}</div>
      {errorAction && <button type="button" className="kd9-btn-primary mt-3 px-4 py-2 rounded-lg text-xs" onClick={()=>navigate(errorAction.path)}>{errorAction.label}</button>}
    </div>}
    {success && <div className="diagnostic-state diagnostic-state-success kd9-card p-6 mb-6 text-center" style={{borderColor:"#22C55E"}}>
      <CheckCircle2 size={30} className="mx-auto mb-3" style={{color:"var(--diag-success)"}}/>
      <h3 className="font-cinzel kd9-gold-text text-xl">DIAGNÓSTICO SOLICITADO</h3>
      <p className="mt-2 text-sm">La Energía KD9 fue debitada y tu solicitud de {success.title} ya está en la Bolsa de Diagnósticos.</p>
      <button className="kd9-btn-primary mt-4 px-5 py-3 rounded-lg" onClick={() => navigate(`/app/mis-diagnosticos?focus=${encodeURIComponent(success.id)}`)}>Ver mis diagnósticos</button>
    </div>}
    {catalogLoading && <div className="diagnostic-state kd9-card p-5 mb-6 text-sm">Cargando catálogo de diagnósticos…</div>}
    {!catalogLoading && catalogError && <div className="diagnostic-state kd9-card p-5 mb-6 text-sm" style={{color:"var(--diag-error)"}}>
      <div>No pudimos cargar el catálogo: {catalogError}</div>
      <button type="button" onClick={loadCatalog} className="kd9-btn-primary mt-3 px-4 py-2 rounded-lg text-xs">Reintentar</button>
    </div>}
    {!catalogLoading && !catalogError && items.length===0 && <div className="diagnostic-state kd9-card p-5 mb-6 text-sm" style={{color:"var(--kd9-text-soft)"}}>No hay diagnósticos disponibles en este momento.</div>}
    <div className="diagnostic-grid grid md:grid-cols-2 lg:grid-cols-3 gap-6">{items.map((it)=><div key={it.slug} className="diagnostic-card diagnostic-request-card kd9-card p-6 flex flex-col">
      <div className="diagnostic-card-media h-40 rounded-lg mb-4 flex items-center justify-center" style={{background:(it.coverImageUrl||it.cover_url)?`url(${it.coverImageUrl||it.cover_url}) center/cover`:"linear-gradient(135deg,var(--kd9-accent-soft),transparent)"}}>{!(it.coverImageUrl||it.cover_url)&&<Sparkles size={40}/>}</div>
      <div className="diagnostic-card-title font-cinzel text-lg">{it.title}</div>
      <p className="diagnostic-description text-sm my-3 flex-1">{it.description}</p>
      <div className="diagnostic-request-meta text-xs mb-4"><Clock size={12} className="inline mr-1"/>{it.duration||"—"} · {(it.material_requirements||[]).length} requisitos de material</div>
      <div className="diagnostic-card-footer flex justify-between items-center"><strong className="diagnostic-price">{fmtEnergy(it.energy_cost)}</strong><button onClick={()=>open(it)} className="diagnostic-primary kd9-btn-primary px-4 py-2 rounded-lg text-xs"><Send size={12} className="inline mr-1"/> Completar solicitud</button></div>
    </div>)}</div>

    {selected && <div className="diagnostic-modal fixed inset-0 z-50 bg-black/80 overflow-y-auto p-3 md:p-8">
      <form noValidate onSubmit={submit} className="diagnostic-form kd9-card max-w-3xl mx-auto p-5 md:p-8 relative">
        <button type="button" onClick={close} className="diagnostic-close absolute right-4 top-4 p-2" aria-label="Cerrar"><X/></button>
        <div className="diagnostic-form-title font-cinzel text-2xl pr-10">{selected.title}</div>
        {resumeId && <div role="status" className="p-3 mb-4 rounded-lg border text-sm" style={{borderColor:"var(--kd9-gold)"}}>
          <strong>Solicitud registrada y Energía KD9 descontada.</strong> Falta enviar o confirmar los materiales.
          Este formulario continúa sobre tu pedido existente y no genera otro cobro.
        </div>}
        <p className="text-sm mt-2 mb-6" style={{color:"var(--kd9-text-soft)"}}>Completa todos los campos obligatorios. Si una carga se interrumpe, podrás reintentar sin crear otra solicitud.</p>

        <label className="diagnostic-consultation text-sm block mb-5"><strong>¿Qué deseas consultar?</strong>
          <textarea minLength={3} maxLength={6000} rows={5} className="kd9-input w-full mt-2 p-3" placeholder="Escribe tu pregunta, situación y qué deseas comprender…" value={consultation} readOnly={Boolean(resumeId)} onChange={(e)=>setConsultation(e.target.value)}/>
        </label>

        {(selected.intake_fields||[]).length>0 && <div className="diagnostic-form-section space-y-5 mb-7">
          <h3 className="font-cinzel kd9-gold-text">INFORMACIÓN DEL ESTUDIO</h3>
          {!resumeId && selected.intake_fields.map((field)=><label key={field.id} className="text-sm block"><strong>{field.label}{field.required&&" *"}</strong>{field.help_text&&<span className="block text-xs mt-1" style={{color:"var(--kd9-text-soft)"}}>{field.help_text}</span>}{renderAnswer(field)}</label>)}
        </div>}

        {(selected.material_requirements||[]).length>0 && <div className="diagnostic-form-section space-y-5 mb-7">
          <h3 className="font-cinzel kd9-gold-text">FOTOS Y MATERIAL</h3>
          {selected.material_requirements.map((req)=><label key={req.id} className="diagnostic-file-field block p-4 border rounded-xl" style={{borderColor:"var(--kd9-border)"}}>
            <div className="flex gap-2 items-center"><FileUp size={16}/><strong>{req.label}{req.required&&" *"}</strong></div>
            {req.instructions&&<div className="text-xs mt-1" style={{color:"var(--kd9-text-soft)"}}>{req.instructions}</div>}
            <div className="text-[11px] mt-1">Cantidad: {req.min_files||0}–{req.max_files}
              {" · "}{savedMaterials.filter((row) => row.requirement_id === req.id).length} archivo(s) guardado(s)
            </div>
            <input className="mt-3 block w-full text-xs" type="file" multiple={Number(req.max_files)>1} accept={acceptFor(req.kind)}
              disabled={savedMaterials.filter((row) => row.requirement_id === req.id).length >= Number(req.max_files || 1)}
              onChange={(e)=>setFiles({...files,[req.id]:Array.from(e.target.files||[])})}/>
            {!!files[req.id]?.length&&<div className="text-xs mt-2" style={{color:"var(--diag-success)"}}>{files[req.id].map(file=>file.name).join(", ")}</div>}
          </label>)}
        </div>}

        <div className="diagnostic-cost-confirmation mb-4 rounded-xl border p-4 text-sm" style={{borderColor:"var(--kd9-border)", background:"rgba(34,211,238,0.04)"}}>
          <div className="flex items-center justify-between gap-4"><span>{resumeId ? "Pago registrado (sin cargo adicional)" : "Costo a confirmar"}</span><strong className="kd9-gold-text">{resumeId ? "0 Energía KD9 adicionales" : fmtEnergy(selected.energy_cost)}</strong></div>
          <div className="mt-1 text-xs" style={{color:"var(--kd9-text-soft)"}}>{resumeId
            ? "El pago se hizo al crear este pedido. Solo completarás los materiales pendientes."
            : "Si Dirección modifica el costo antes del envío, KD9 no debitará un valor distinto sin mostrártelo primero."}</div>
        </div>
        <button disabled={busy} className="diagnostic-primary diagnostic-submit kd9-btn-primary w-full py-4 rounded-lg">{busy ? progress || "Procesando…" : resumeId ? "Completar y enviar a la Bolsa · sin nuevo cobro" : `Solicitar · ${fmtEnergy(selected.energy_cost)}`}</button>
      </form>
    </div>}
  </PageShell></div>;
};
export default Estudios;
